/**
 * SpinalEase — Post-Op Spinal Recovery experience
 *
 * A bespoke healing immersion for someone recovering from spinal
 * stenosis / decompression surgery. Anatomically realistic lumbar
 * spine visualisation + user-recorded narration + post-op affirmations.
 *
 * Steps: Entry → Affirmation Preview → Immersion (7 min) → Completion
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Users, Waves, LineChart } from 'lucide-react';
import HealingEntry from './HealingEntry';
import GentleMovement from './GentleMovement';
import PositioningGuide from './PositioningGuide';
import AffirmationSelection from './AffirmationSelection';
import HealingImmersion from './HealingImmersion';
import HealingCompletion from './HealingCompletion';
import SpinalEaseFamilyCircle from './SpinalEaseFamilyCircle';
import RecoveryJournalCheckin from './RecoveryJournalCheckin';
import HealingCircleOpening from './HealingCircleOpening';
import JourneyLengthSelector from './JourneyLengthSelector';
import useAudioMode from '../../hooks/useAudioMode';

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITION = 'spinal-ease';
const CONDITION_LABEL = 'Spinal Healing & Recovery';

const SpinalEase = () => {
  const navigate = useNavigate();
  const [, setMode] = useAudioMode();
  const [step, setStep] = useState(0);
  const [stepsCompleted, setStepsCompleted] = useState([]);
  const [startTime] = useState(Date.now());
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);
  const [journalDone, setJournalDone] = useState(false);
  const [journeyLength, setJourneyLength] = useState(null);
  const selectedAffirmationsRef = useRef([]);

  const markStep = useCallback((stepName) => {
    setStepsCompleted(prev => [...new Set([...prev, stepName])]);
  }, []);

  const handleSave = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const duration = Math.round((Date.now() - startTime) / 1000);
    try {
      await fetch(`${API}/api/healing/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          condition: CONDITION,
          duration_seconds: duration,
          steps_completed: stepsCompleted,
          journey_length: journeyLength?.id || null,
        }),
      });
    } catch (e) {
      console.error('Failed to save session:', e);
    }
  }, [startTime, stepsCompleted, journeyLength]);

  return (
    <>
      <AnimatePresence mode="wait">
        {step === 0 && (
          <HealingEntry
            key="entry"
            title={CONDITION_LABEL}
            subtitle="Cinematic decompression, breath and voice — for every kind of spine"
            description="A crystal spine suspended in soft golden light. Guided breath, decompression imagery and the voice of someone you love."
            onBegin={(mode) => { setMode(mode); setStep(3); }}
            onBack={() => navigate('/healing')}
            area="healing_spinal"
            glow="rgba(200,170,110,0.28)"
            accent="rgba(220,180,110,0.5)"
          />
        )}

        {step === 1 && (
          <GentleMovement
            key="movement"
            condition={CONDITION}
            onComplete={() => { markStep('movement'); setStep(2); }}
            onSkip={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <PositioningGuide
            key="positioning"
            onReady={() => { markStep('positioning'); setStep(3); }}
          />
        )}

        {step === 3 && (
          <AffirmationSelection
            key="affirmations"
            condition={CONDITION}
            onContinue={(affirmations) => {
              selectedAffirmationsRef.current = affirmations;
              markStep('affirmations');
              setStep(3.5);
            }}
          />
        )}

        {step === 3.5 && (
          <HealingCircleOpening
            key="circle-open"
            onDone={() => { markStep('healing-circle'); setStep(3.75); }}
          />
        )}

        {step === 3.75 && (
          <JourneyLengthSelector
            key="length-select"
            onSelect={(L) => {
              setJourneyLength(L);
              markStep(`length-${L.id}`);
              setStep(4);
            }}
          />
        )}

        {step === 4 && (
          <HealingImmersion
            key="immersion"
            condition={CONDITION}
            affirmations={selectedAffirmationsRef.current}
            journeyLength={journeyLength?.id || null}
            journeyDurationSeconds={journeyLength?.duration_seconds || null}
            onComplete={() => { markStep('immersion'); setStep(5); }}
          />
        )}

        {step === 5 && (
          <>
            <HealingCompletion
              key="completion"
              onSave={() => { handleSave(); }}
              onRepeat={() => { setJournalDone(false); setStep(3); }}
              onHome={() => navigate('/healing')}
            />
            {/* Journal check-in — soft overlay one-time-per-session. */}
            <AnimatePresence>
              {!journalDone && (
                <motion.div
                  key="journal-overlay"
                  className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 overflow-y-auto"
                  style={{ background: 'rgba(6,10,8,0.72)', backdropFilter: 'blur(10px)' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  data-testid="journal-overlay"
                >
                  <RecoveryJournalCheckin
                    sessionId={null}
                    source="post-session"
                    onDone={() => setJournalDone(true)}
                    onSkip={() => setJournalDone(true)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </AnimatePresence>

      {/* Entry-screen action chips. Stacked so any of the three fits comfortably
          on a phone: Family Circle · Emergency Nerve Ease · Journey (trends). */}
      {step === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center justify-center gap-2 px-4"
        >
          <motion.button
            onClick={() => setVoicePanelOpen(true)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs"
            style={{
              background: 'rgba(200,170,110,0.12)',
              border: '1px solid rgba(200,170,110,0.28)',
              color: 'rgba(255,240,210,0.85)',
              backdropFilter: 'blur(8px)',
              fontFamily: "'Manrope', sans-serif",
              letterSpacing: '0.05em',
            }}
            data-testid="spinal-family-circle-chip"
          >
            <Users size={12} />
            Healing Circle
          </motion.button>

          <motion.button
            onClick={() => navigate('/healing/spinal-ease/emergency')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs"
            style={{
              background: 'rgba(180,220,255,0.10)',
              border: '1px solid rgba(180,220,255,0.28)',
              color: 'rgba(220,240,255,0.9)',
              backdropFilter: 'blur(8px)',
              fontFamily: "'Manrope', sans-serif",
              letterSpacing: '0.05em',
            }}
            data-testid="spinal-emergency-chip"
          >
            <Waves size={12} />
            Ease a flare-up now
          </motion.button>

          <motion.button
            onClick={() => navigate('/healing/spinal-ease/journal')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs"
            style={{
              background: 'rgba(180,210,160,0.10)',
              border: '1px solid rgba(180,210,160,0.28)',
              color: 'rgba(220,240,200,0.9)',
              backdropFilter: 'blur(8px)',
              fontFamily: "'Manrope', sans-serif",
              letterSpacing: '0.05em',
            }}
            data-testid="spinal-journey-chip"
          >
            <LineChart size={12} />
            Journey so far
          </motion.button>
        </motion.div>
      )}

      <AnimatePresence>
        {voicePanelOpen && (
          <SpinalEaseFamilyCircle onClose={() => setVoicePanelOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default SpinalEase;
